/**
 * Finds the element a table of contents fragment points to: the element with that id, or
 * the first one with that name (older EPUBs anchor with `<a name>`).
 * @param {Document} document - The resource's document
 * @param {string} fragment - Decoded fragment, without `#`
 * @returns {Element|null}
 */
export function findEpubTocTarget(document, fragment) {
    return document.getElementById(fragment) || document.getElementsByName(fragment)[0] || null;
}

/**
 * Sorts table of contents anchors in the order their elements appear in the document.
 * Anchors on the same element keep the order of their entries in the table of contents.
 * @param {Array<{element: Element, sourceOrder: number}>} anchors
 * @returns {Array<{element: Element, sourceOrder: number}>} The same array, sorted
 */
export function sortEpubTocAnchorsByDocumentPosition(anchors) {
    return anchors.sort((left, right) => {
        if (left.element === right.element) return left.sourceOrder - right.sourceOrder;
        const position = left.element.compareDocumentPosition(right.element);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return left.sourceOrder - right.sourceOrder;
    });
}

/**
 * Finds the element of each table of contents anchor in a resource's document and returns
 * the anchors found, in the order their elements appear. An anchor whose element is missing
 * is left out, and so is a later anchor with the key of one already found.
 * @param {Document|null|undefined} document - The resource's document
 * @param {Array<{key: string, fragment: string, sourceOrder?: number}>} anchors - In table of
 *   contents order; `sourceOrder` defaults to the anchor's index
 * @param {(document: Document, fragment: string) => Element|null} [findTarget=findEpubTocTarget]
 * @returns {Array<{key: string, element: Element, sourceOrder: number}>} Empty without a document
 */
export function locateEpubTocAnchors(document, anchors, findTarget = findEpubTocTarget) {
    if (!document) return [];

    const located = [];
    const locatedKeys = new Set();
    anchors.forEach(({ key, fragment, sourceOrder }, index) => {
        if (locatedKeys.has(key)) return;

        const element = findTarget(document, fragment);
        if (!element) return;

        locatedKeys.add(key);
        located.push({ key, element, sourceOrder: sourceOrder ?? index });
    });

    return sortEpubTocAnchorsByDocumentPosition(located);
}

/**
 * Lists the keys of the anchors that start at or before an element.
 * @param {Array<{key: string, element: Element}>} anchors - In document order
 * @param {Element|null} element
 * @returns {string[]} In document order; empty without an element
 */
export function listStartedEpubTocAnchorKeys(anchors, element) {
    if (!element) return [];

    return anchors.filter(anchor => hasEpubTocAnchorStarted(anchor.element, element)).map(({ key }) => key);
}

/**
 * Whether a table of contents anchor starts at or before an element: the anchor is the
 * element, contains it, or comes before it in the document.
 * @param {Element} anchorElement
 * @param {Element} element
 * @returns {boolean}
 */
export function hasEpubTocAnchorStarted(anchorElement, element) {
    if (anchorElement.contains(element)) return true;

    return Boolean(anchorElement.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
}
